import { useEffect, useRef, useState, lazy, Suspense } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import styles from './MinimalHome.module.css';
import Navbar from '../components/Navbar/Navbar';
import AboutSection from './AboutSection';

import heroD1 from '../images/afriat-hero-d1.webp';
import heroD2 from '../images/afriat-hero-d2.webp';
import heroD3 from '../images/afriat-hero-d3.webp';
import heroD4 from '../images/afriat-hero-d4.webp';
import heroD5 from '../images/afriat-hero-d5.webp';
import heroD6 from '../images/afriat-hero-d6.webp';
import heroD7 from '../images/afriat-hero-d7.webp';
import heroM1 from '../images/afriat-hero-m1.webp';
import heroM2 from '../images/afriat-hero-m2.webp';
import heroM3 from '../images/afriat-hero-m3.webp';
import heroM4 from '../images/afriat-hero-m4.webp';
import heroM5 from '../images/afriat-hero-m5.webp';
import heroM6 from '../images/afriat-hero-m6.webp';
import aboutImg from '../images/afriat-a-house 1.webp';

const FlagshipProject = lazy(() => import('../components/Project/ProjectShowcase'));
const WorkProcess = lazy(() => import('./WorkProcess'));
const Testimonials = lazy(() => import('./Testimonials'));
const FAQ = lazy(() => import('../components/FAQ/FAQ'));
const Footer = lazy(() => import('../components/Footer/Footer'));
const FloatingWhatsApp = lazy(() => import('../components/FloatingWhatsApp/FloatingWhatsApp'));

gsap.registerPlugin(ScrollTrigger);

const WHATSAPP = 'https://wa.me/972548145494?text=%D7%94%D7%99%D7%99%20%D7%97%D7%9F%2C%20%D7%94%D7%92%D7%A2%D7%AA%D7%99%20%D7%9E%D7%94%D7%90%D7%AA%D7%A8%20%D7%A9%D7%9C%D7%9A%2C%20%D7%90%D7%A9%D7%9E%D7%97%20%D7%9C%D7%A9%D7%9E%D7%95%D7%A2%20%D7%A2%D7%95%D7%93%20%D7%A2%D7%9C..';

const Loader = () => (
  <div className={styles.loadingContainer}>
    <div className={styles.loadingScene}>
      <svg className={styles.logoSvg} viewBox="0 0 300 260" fill="none">
        <rect className={styles.logoOuter} x="70" y="16" width="190" height="190" stroke="#1a365d" strokeWidth="2.2" fill="none" />
        <rect className={styles.logoInner} x="110" y="56" width="110" height="130" stroke="#1a365d" strokeWidth="2" fill="none" />
        <line className={styles.logoDiagonal} x1="42" y1="196" x2="262" y2="30" stroke="#1a365d" strokeWidth="2" />
        <line className={styles.logoLine1} x1="150" y1="56" x2="150" y2="186" stroke="#1a365d" strokeWidth="1.8" />
        <line className={styles.logoLine2} x1="185" y1="100" x2="185" y2="186" stroke="#1a365d" strokeWidth="1.8" />
        <line className={styles.logoLine3} x1="110" y1="130" x2="185" y2="130" stroke="#1a365d" strokeWidth="1.8" />
      </svg>
      <div className={styles.loadingBrand}>
        <span className={styles.loadingBrandName}>AFRIAT ARCHITECTS</span>
      </div>
      <div className={styles.loadingProgress}>
        <div className={styles.loadingProgressBar} />
      </div>
    </div>
  </div>
);

const AboutBlurb = () => {
  const innerRef = useRef(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    if (!innerRef.current) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        Array.from(innerRef.current.children),
        { opacity: 0, y: 28 },
        {
          opacity: 1, y: 0,
          stagger: 0.13,
          duration: 1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 72%',
            toggleActions: 'play none none none',
          },
        }
      );
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section className={styles.about} ref={sectionRef} id="about" dir="rtl">
      <div className={styles.aboutText} ref={innerRef}>
        <span className={styles.aboutLabel}>AFRIAT ARCHITECTURE</span>
        <p className={styles.aboutBody}>
          כל פרויקט נולד מתוך הקשבה עמוקה לאנשים שעתידים לחיות בו.
        </p>
        <a href={WHATSAPP} className={styles.aboutCta} target="_blank" rel="noopener noreferrer">
          יצירת קשר
        </a>
      </div>
      <div className={styles.aboutImageWrap}>
        <img src={aboutImg} alt="AFRIAT ARCHITECTURE" className={styles.aboutImage} loading="lazy" decoding="async" />
      </div>
    </section>
  );
};

const MinimalHome = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [currentIdx, setCurrentIdx] = useState(0);
  const isMobile = window.innerWidth <= 768;
  const imagesRef = useRef([]);
  const zoomRef = useRef(null);

  const desktopImages = [heroD1, heroD2, heroD3, heroD4, heroD5, heroD6, heroD7];
  const mobileImages = [heroM1, heroM2, heroM3, heroM4, heroM5, heroM6];
  const images = isMobile ? mobileImages : desktopImages;

  useEffect(() => {
    Promise.all(
      images.map(src => new Promise(r => {
        const img = new Image();
        img.onload = r;
        img.onerror = r;
        img.src = src;
      }))
    ).then(() => {
      setIsLoading(false);
      setTimeout(() => ScrollTrigger.refresh(), 300);
      setTimeout(() => ScrollTrigger.refresh(), 1000);
    });
  }, []);

  useEffect(() => {
    if (isLoading) return;
    const id = setInterval(() => setCurrentIdx(p => (p + 1) % images.length), 6000);
    return () => clearInterval(id);
  }, [isLoading, images.length]);

  useEffect(() => {
    if (isLoading) return;
    imagesRef.current.forEach((el, i) => {
      if (!el) return;
      gsap.to(el, { opacity: i === currentIdx ? 1 : 0, duration: 1.8, ease: 'power2.inOut' });
    });
    if (zoomRef.current) zoomRef.current.kill();
    const active = imagesRef.current[currentIdx];
    if (active) {
      gsap.set(active, { scale: 1.08 });
      zoomRef.current = gsap.to(active, { scale: 1, duration: 7, ease: 'none' });
    }
  }, [currentIdx, isLoading]);

  if (isLoading) return <Loader />;

  return (
    <>
      <Navbar />

      <section className={styles.hero}>
        <div className={styles.heroImages}>
          {images.map((src, i) => (
            <img
              key={i}
              ref={el => { imagesRef.current[i] = el; }}
              src={src}
              alt=""
              aria-hidden="true"
              className={styles.heroImg}
              style={{ opacity: i === 0 ? 1 : 0 }}
            />
          ))}
        </div>
        <div className={styles.heroOverlay} />

        <div className={styles.heroContent}>
          <span className={styles.heroWord}>AFRIAT</span>
          <span className={styles.heroSub}>ARCHITECTS</span>
        </div>

        <div className={styles.heroScroll}>
          <div className={styles.heroScrollLine} />
        </div>
      </section>

      <Suspense fallback={null}>
        <FlagshipProject />
        <WorkProcess />
      </Suspense>

      <AboutSection />

      <Suspense fallback={null}>
        <Testimonials />
        <FAQ />
        <Footer />
        <FloatingWhatsApp />
      </Suspense>
    </>
  );
};

export default MinimalHome;
